import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";

import API, { API_URL, BASE, showNetworkErrorToast } from "Utils/api";

import { addNewVehicle, updateVehicle } from ".";

type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

// Safety cap for the pagination loop. 50 pages x 100/page is far beyond any
// realistic fleet size; stops a runaway loop if the backend ever returns a
// self-referential or cyclic `next` link.
const MAX_VEHICLE_PAGES = 50;

// Validate the backend's `next` link and return it as an absolute URL. Absolute
// URLs pointing anywhere other than API_URL are rejected so a compromised
// backend can't redirect the authenticated request to an attacker host. We
// pass the absolute URL straight through because axios bypasses `baseURL`
// for absolute URLs — stripping to pathname+search would double the
// configured `/api/v1` baseURL prefix and 404.
const resolveNextUrl = (next: string, apiOrigin: string): string => {
  const parsed = new URL(next, API_URL);
  if (parsed.origin !== apiOrigin) {
    throw new Error(
      `Pagination next URL has unexpected origin: ${parsed.origin}`
    );
  }
  return parsed.toString();
};

// Cycle-detection key: the URL axios will actually request. Absolute URLs
// pass through; relative URLs get BASE prepended. This keeps the initial
// relative "/vehicles/" comparable to subsequent absolute `next` URLs.
const canonicalize = (url: string): string =>
  /^https?:\/\//.test(url)
    ? url
    : `${BASE}${url.startsWith("/") ? "" : "/"}${url}`;

export const fetchGetVehicles = createAsyncThunk<Vehicle[]>(
  "vehicles/fetchGetVehicles",
  async () => {
    try {
      const apiOrigin = new URL(API_URL).origin;
      const vehicles: Vehicle[] = [];
      const visited = new Set<string>();
      let url: string | null = "/vehicles/";

      while (url) {
        const key = canonicalize(url);
        if (visited.has(key)) {
          throw new Error("Pagination cycle detected fetching vehicles");
        }
        if (visited.size >= MAX_VEHICLE_PAGES) {
          throw new Error("Pagination exceeded MAX_VEHICLE_PAGES");
        }
        visited.add(key);

        const response: AxiosResponse<PaginatedResponse<Vehicle>> =
          await API.get(url);
        vehicles.push(...(response.data.results ?? []));
        url = response.data.next
          ? resolveNextUrl(response.data.next, apiOrigin)
          : null;
      }
      return vehicles;
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const fetchPostVehicles = createAsyncThunk<void, VehicleCore>(
  "vehicles/fetchPostVehicles",
  async (data, thunkAPI) => {
    try {
      const response = await API.post("/vehicles/", {
        ...data,
      });
      thunkAPI.dispatch(addNewVehicle(response.data));
      toast.success("Vehicle added successfully");
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const fetchPutVehiclesId = createAsyncThunk<
  void,
  { id: string; data: VehicleCore }
>(
  "vehicles/fetchPutVehiclesId",
  async ({ id, data }: { id: string; data: VehicleCore }, thunkAPI) => {
    try {
      const response = await API.patch(`/vehicles/${id}/`, data);
      await thunkAPI.dispatch(updateVehicle(response.data));
      toast.success(`Vehicle ${data.name} edited successfully`);
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);
